import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import { Breadcrumb, Descriptions, Card, Modal, message } from 'antd';
import SvgFormat from 'common/helpers/svgFormat';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { formatUppercaseLetters, processBookingSystem } from '~helpers/change-log';
import { AccountPhoto as AccountPhotoIcon } from 'common/components/svgs';
import formatPrice from '~helpers/currency';
import { stringDateFormat, getGender } from '~pages/dashboard/reconciliation/utils';
import { closeActiveOpportunityCases } from '~actions/reconciliation/bookinglist';
import nationality from '~helpers/nationality';
import { showPassportName } from '~helpers';
import authControl from '~components/auth-control';
import { platformEntity, entityAction } from '~constants';
import ChangeBookingStatusModal from '~pages/dashboard/reconciliation/booking-details/statusModal';
import CommissionDetail from '~pages/dashboard/reconciliation/booking-details/commission';
import { secondaryLandlordBookingStatus } from '~constants/reconciliation-booking';
import ReconciliationOpportunityModal from '~components/reconciliation-opportunity-modal';
import useOpportunityDetails from '~pages/dashboard/reconciliation/booking-details/useOpportunityDetails';
import InvoiceEditor from '~pages/dashboard/reconciliation/booking-details/invoice-editor';
import EditNotes from '~pages/dashboard/reconciliation/booking-details/edit-notes';
import generatePath from '~settings/routing';

const Index = ({ match }) => {
  const { t } = useTranslation();
  const { opportunityId } = match.params;
  const {
    handleToggleEditOpportunityModal,
    handleClickConfirm,
    handleChangeBookingStatus,
    setShowStatusModal,
    setShowCloseCaseModal,
    setChangeBookingBtnDisabled,
    setLandlordBookingStatus,
    setUpdateCounter,
    showStatusModal,
    isShowCloseCaseModal,
    isShowBookingModal,
    pageInfo,
    opportunityDetail,
    updateCounter,
    setInvoiceEditor,
    showInvoiceEditor,
    showEditNotes,
    setShowEditNotes,
    fetchReconciliationOpportunityDetails,
  } = useOpportunityDetails({ opportunityId });

  const calculateWeeksAndDaysBetweenDates = useMemo(() => {
    const finalMoveInDate = get(opportunityDetail, 'finalMoveInDate');
    const finalMoveOutDate = get(opportunityDetail, 'finalMoveOutDate');
    if (finalMoveInDate && finalMoveOutDate) {
      const startMoment = moment(finalMoveInDate);
      const endMoment = moment(finalMoveOutDate);
      const weeks = endMoment.diff(startMoment, 'weeks');
      const remainingDays = endMoment.diff(startMoment, 'days') % 7;
      if (weeks <= 0) {
        return remainingDays > 0 ? `${remainingDays} days` : '-';
      } else {
        const daysTotal = weeks * 7 + remainingDays;
        const daysString = remainingDays > 0 ? ` ${remainingDays} days` : '';
        return `${weeks} weeks${daysString} (${daysTotal} days)`;
      }
    }
    return '-';
  }, [opportunityDetail?.finalMoveInDate, opportunityDetail?.finalMoveOutDate]);

  return opportunityDetail ? (
    <div className="reconciliation-booking">
      <div className="reconciliation-booking__card">
        <div className="nav">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={generatePath('reconciliation.bookingWithConsultant')}>
                {t('cms.reconciliation.list.title')}
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/reconciliation/booking-with-consultant/${match.params.opportunityId}`}>
                {get(opportunityDetail, 'referenceId', '-')}
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className="discribe--wapper ">
          <Descriptions
            title={`${showPassportName(get(opportunityDetail, 'student', {}))}’s booking`}
          ></Descriptions>
          {/* <div className="btns-box">
            <div className="reconciliation-owner">
              {t('cms.reconciliation.list.table.reconciliation_owner')}:&nbsp;&nbsp;
              <span>{get(opportunityDetail, 'student.emailAddress') || '-'}</span>
            </div>
          </div> */}
        </div>
      </div>

      <div className="reconciliation-booking__tabs">
        <div className="reconciliation-booking__content" id="booking_details">
          <div className="box-cards">
            <div className="booking-details-container">
              <div className="booking-details-container-item">
                <div className="icon-box">
                  <SvgFormat
                    type="reconciliation-booking-details-icon"
                    className={'reconciliation-booking-details-icon'}
                  />
                </div>

                <div className="item-box">
                  <span>{t('cms.reconciliation.list.table.filter.booking_reference')}</span>
                  <p>{get(opportunityDetail, 'referenceId', '-')}</p>
                </div>
              </div>

              <div className="booking-details-container-item">
                <div className="item-box">
                  <span>{t('cms.reconciliation.landlord.table.landlord_booking_status')}</span>
                  <p>
                    {formatUppercaseLetters(
                      get(opportunityDetail, 'landlordBookingStatus')
                        ?.toLowerCase?.()
                        ?.split?.('_')
                        ?.join?.(' '),
                    ) || '-'}
                    <div className="edit-box" onClick={handleChangeBookingStatus}>
                      <SvgFormat
                        type="reconciliation-edit-icon"
                        className={'reconciliation-edit-icon'}
                      />
                    </div>
                  </p>
                </div>
              </div>

              <div className="booking-details-container-item">
                <div className="item-box">
                  <span>
                    {t('cms.reconciliation.landlord.table.secondary_landlord_booking_status')}
                  </span>
                  <p>
                    {get(opportunityDetail, 'secondaryLandlordBookingStatus')
                      ? formatUppercaseLetters(
                          get(opportunityDetail, 'secondaryLandlordBookingStatus')
                            ?.toLowerCase?.()
                            ?.split?.('_')
                            ?.join?.(' '),
                        )
                      : '-'}
                    <div className="edit-box" onClick={handleChangeBookingStatus}>
                      <SvgFormat
                        type="reconciliation-edit-icon"
                        className={'reconciliation-edit-icon'}
                      />
                    </div>
                  </p>
                </div>
              </div>
              <div className="line"></div>
            </div>
            <div className="booking-details-list">
              <div className="booking-details-list-li">
                <div className="booking-details-list-li-title">
                  {t('cms.reconciliation.list.table.filter.created_date')}
                </div>
                <div className="booking-details-list-li-value">
                  {stringDateFormat(get(opportunityDetail, 'createdAt'))}
                </div>
              </div>
              <div className="booking-details-list-li">
                <div className="booking-details-list-li-title">
                  {t('cms.reconciliation.booking.details.header.Completeat')}
                </div>
                <div className="booking-details-list-li-value">
                  {stringDateFormat(get(opportunityDetail, 'completedAt'))}
                </div>
              </div>
              <div className="booking-details-list-li">
                <div className="booking-details-list-li-title">
                  {t('cms.reconciliation.list.table.filter.destination_market')}
                </div>
                <div className="booking-details-list-li-value">
                  {get(opportunityDetail, 'city.country.market') || '-'}
                </div>
              </div>
              <div className="booking-details-list-li">
                <div className="booking-details-list-li-title">
                  {t('cms.reconciliation.list.table.destination_city')}
                </div>
                <div className="booking-details-list-li-value">
                  {get(opportunityDetail, 'city.name')
                    ? `${get(opportunityDetail, 'city.name')}, `
                    : ''}
                  {get(opportunityDetail, 'city.country.name')}
                </div>
              </div>
              <div className="booking-details-list-li">
                <div className="booking-details-list-li-title">
                  {t('cms.reconciliation.booking.details.header.Currencyapplied')}
                </div>
                <div className="booking-details-list-li-value">
                  {get(opportunityDetail, 'completedXbooking.currency') || '-'}
                </div>
              </div>
              <div className="booking-details-list-li">
                <div className="booking-details-list-li-title">
                  {t('cms.reconciliation.list.table.booking_consultant')}
                </div>
                <div className="booking-details-list-li-value">
                  {get(opportunityDetail, 'ownedBy') || '-'}
                </div>
              </div>

              <div className="booking-details-list-li">
                <div className="booking-details-list-li-title">
                  {t('cms.reconciliation.list.table.booking_system')}
                </div>
                <div className="booking-details-list-li-value">
                  {processBookingSystem(get(opportunityDetail, 'landlordApplicationSystem'))}
                </div>
              </div>
            </div>
            <Card
              headStyle={{
                padding: 0,
                color: 'rgba(0, 0, 0, 0.45)',
                fontSize: '14px',
                borderBottom: 'none',
              }}
              bodyStyle={{
                padding: '18px 24px',
                background: '#F5F5F5',
              }}
              title={
                <div className="card-head-title">
                  <SvgFormat
                    type="reconciliation-schedule-icon"
                    className={'reconciliation-schedule-icon'}
                  />
                  {t('cms.reconciliation.booking.details.cards.label.Bookingdetails')}
                </div>
              }
              bordered={false}
              className="booking-card"
              extra={
                <span className="booking-card__extra-container">
                  <span className="booking-card__extra-remider"></span>
                  <div className="edit-btn" onClick={handleToggleEditOpportunityModal}>
                    Edit
                  </div>
                </span>
              }
            >
              <div className="booking-details-list booking-details-box">
                <div className="booking-details-list-li">
                  <div className="booking-details-list-li-title">
                    {t('cms.reconciliation.booking.details.info.label.movein')}
                  </div>
                  <div className="booking-details-list-li-value">
                    {stringDateFormat(get(opportunityDetail, 'finalMoveInDate'))}
                  </div>
                </div>
                <div className="booking-details-list-li">
                  <div className="booking-details-list-li-title">
                    {t('cms.reconciliation.booking.details.info.label.moveout')}
                  </div>
                  <div className="booking-details-list-li-value">
                    {stringDateFormat(get(opportunityDetail, 'finalMoveOutDate'))}
                  </div>
                </div>
                <div className="booking-details-list-li">
                  <div className="booking-details-list-li-title">
                    {t('cms.reconciliation.list.table.listing_price')}
                  </div>
                  <div className="booking-details-list-li-value">
                    {get(opportunityDetail, 'completedXbooking.unitPrice')
                      ? formatPrice(
                          get(opportunityDetail, 'completedXbooking.unitPrice'),
                          get(opportunityDetail, 'completedXbooking.currency'),
                        ) +
                        `/${
                          get(
                            opportunityDetail,
                            'completedXbooking.billingCycle',
                          )?.toLowerCase() === 'weekly'
                            ? 'week'
                            : 'month'
                        }`
                      : '-'}
                  </div>
                </div>
                <div className="booking-details-list-li">
                  <div className="booking-details-list-li-title">
                    {t('cms.reconciliation.booking.details.info.label.Finalprice')}
                  </div>
                  <div className="booking-details-list-li-value">
                    {formatPrice(
                      get(opportunityDetail, 'finalPrice', 0),
                      get(opportunityDetail, 'completedXbooking.currency'),
                    )}
                  </div>
                </div>
              </div>
            </Card>
            <CommissionDetail
              opportunityDetail={opportunityDetail}
              handleClickEditInvoice={() => {
                setInvoiceEditor(true);
              }}
              refreshDetails={() => {
                fetchReconciliationOpportunityDetails();
              }}
            />

            <div className="product-card-container">
              <Card
                bordered={false}
                headStyle={{
                  padding: 0,
                  color: 'rgba(0, 0, 0, 0.45)',
                  fontSize: '14px',
                  borderBottom: 'none',
                }}
                bodyStyle={{
                  padding: '0',
                  background: '#F5F5F5',
                }}
                title={
                  <div className="card-head-title">
                    <SvgFormat
                      type="reconciliation-home-icon"
                      className={'reconciliation-home-icon'}
                    />
                    {t('cms.contract.confirm.block_title.property_information')}
                  </div>
                }
                className="product-card"
                extra={
                  <a className="mainbtn changeproduct">
                    {t('cms.reconciliation.booking.details.info.label.Changeproduct')}
                  </a>
                }
              >
                <div className="product-card-box">
                  <div className="product-card-item">
                    <div className="name">{t('cms.reconciliation.list.table.property_name')}:</div>
                    <div className="value">
                      {get(opportunityDetail, 'completedXbooking.propertyName') || '-'}
                    </div>
                  </div>

                  <div className="product-card-item">
                    <div className="name">{t('cms.reconciliation.list.table.room_name')}:</div>
                    <div className="value">
                      {get(opportunityDetail, 'completedXbooking.roomName') || '-'}
                    </div>
                  </div>

                  <div className="product-card-item">
                    <div className="name">{t('cms.reconciliation.list.table.landlord_name')}:</div>
                    <div className="value">
                      {get(opportunityDetail, 'completedXbooking.landlordName') || '-'}
                    </div>
                  </div>

                  <div className="product-card-item">
                    <div className="name">{t('cms.reconciliation.list.table.landlord_ID')}:</div>
                    <div className="value">
                      {get(opportunityDetail, 'completedXbooking.landlordId') || '-'}
                    </div>
                  </div>
                </div>

                <div className="date-box">
                  <div className="date-item">
                    <div className="name">
                      {t('cms.reconciliation.booking.details.table.label.Tendency.movein')}:
                    </div>
                    <div className="value">{get(opportunityDetail, 'finalMoveInDate') || '-'}</div>
                  </div>
                  <div className="date-item">
                    <div className="name">{t('cms.property.commission.list.tenancy_length')}:</div>
                    <div className="value tenancy-value">
                      <span>{calculateWeeksAndDaysBetweenDates}</span>
                    </div>
                  </div>
                  <div className="date-item">
                    <div className="name">
                      {t('cms.reconciliation.booking.details.table.label.Tendency.moveout')}:
                    </div>
                    <div className="value">{get(opportunityDetail, 'finalMoveOutDate') || '-'}</div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
          <div className="booking-userinfo">
            <If
              condition={
                get(opportunityDetail, 'secondaryLandlordBookingStatus') !==
                secondaryLandlordBookingStatus.CASE_CLOSED
              }
            >
              <div className="user-info-box">
                <Card
                  headStyle={{
                    padding: 0,
                  }}
                  bodyStyle={{
                    padding: 0,
                  }}
                  bordered={false}
                >
                  <div className="user-header-box">
                    <Card.Meta
                      avatar={<AccountPhotoIcon className="account-photo-icon" />}
                      title={showPassportName(get(opportunityDetail, 'student', {}))}
                      description={get(opportunityDetail, 'student.emailAddress')}
                    />
                  </div>
                  <div className="user-body-box">
                    <Descriptions layout="vertical" column={1} colon={false}>
                      <Descriptions.Item
                        label={t(
                          'cms.reconciliation.booking.details.userinfo.label.Passportnumber',
                        )}
                      >
                        {get(opportunityDetail, 'student.passportNumber') || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={t('cms.reconciliation.booking.details.userinfo.label.Phone')}
                      >
                        {get(opportunityDetail, 'student.phoneNumber') || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={t('cms.reconciliation.booking.details.userinfo.label.Birthday')}
                      >
                        {stringDateFormat(get(opportunityDetail, 'student.dateOfBirth'))}
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={t('cms.reconciliation.booking.details.userinfo.label.Gender')}
                      >
                        {getGender(get(opportunityDetail, 'student.gender'))}
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={t('cms.reconciliation.booking.details.userinfo.label.Nationality')}
                      >
                        {get(
                          nationality.list.find(
                            f => f.code === get(opportunityDetail, 'student.nationality'),
                          ),
                          'name',
                        ) || '-'}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={t('cms.reconciliation.list.table.destination_city')}
                      >
                        {get(opportunityDetail, 'student.city') || '-'}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={t(
                          'cms.reconciliation.booking.details.userinfo.label.Destinationuniversity',
                        )}
                      >
                        {get(opportunityDetail, 'student.destinationUniversity')
                          ? get(opportunityDetail, 'student.destinationUniversity').replace(
                              /-/gi,
                              ' ',
                            )
                          : '-'}
                      </Descriptions.Item>
                    </Descriptions>
                  </div>
                </Card>
              </div>
            </If>

            <div className="notes-box">
              <Card
                bordered={false}
                headStyle={{
                  padding: '8px 32px 0 32px',
                  color: 'rgba(0, 0, 0, 0.45)',
                  fontSize: '14px',
                  borderBottom: 'none',
                  fontWeight: 'bold',
                }}
                bodyStyle={{
                  padding: '12px 32px 24px 32px',
                }}
                title={
                  <div className="card-head-title">
                    <SvgFormat type="supply-team-notes-icon" className={'supply-team-notes-icon'} />
                    {t('cms.reconciliation.list.table.supply_team_notes')}
                  </div>
                }
              >
                <div className="notes-content">
                  {get(opportunityDetail, 'opportunityNote.reconciliationNote')}
                  {!get(opportunityDetail, 'opportunityNote.reconciliationNote') && (
                    <div className="notes-add">
                      {t('cms.reconciliation.supply_team_notes.add_text')}
                    </div>
                  )}
                  <div
                    className="edit-btn"
                    onClick={() => {
                      setShowEditNotes(true);
                    }}
                  >
                    {get(opportunityDetail, 'opportunityNote.reconciliationNote') ? 'Edit' : 'Add'}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
      {showStatusModal && (
        <ChangeBookingStatusModal
          status={{
            landlordBookingStatus: get(opportunityDetail, 'landlordBookingStatus'),
            secondaryLandlordBookingStatus: get(
              opportunityDetail,
              'secondaryLandlordBookingStatus',
            ),
          }}
          opportunityId={get(opportunityDetail, 'id')}
          payload={[showStatusModal, setShowStatusModal, setLandlordBookingStatus]}
        />
      )}
      {
        <Modal
          visible={isShowCloseCaseModal}
          icon={null}
          okText={pageInfo.confirm.okText}
          okType={'primary'}
          destroyOnClose={true}
          cancelText={pageInfo.confirm.cancelText}
          maskClosable={false}
          onCancel={() => {
            setChangeBookingBtnDisabled(false);
            setShowCloseCaseModal(false);
          }}
          onOk={() => {
            closeActiveOpportunityCases(
              { opportunityId: get(opportunityDetail, 'id') },
              closeResult => {
                if (closeResult.closeActiveOpportunityCases) {
                  message.success(pageInfo.confirm.succ);
                } else {
                  message.error(pageInfo.confirm.failed);
                }
                setUpdateCounter(updateCounter + 1);
                setChangeBookingBtnDisabled(false);
                setShowCloseCaseModal(false);
              },
            );
          }}
        >
          <div className="reconciliation-bkdetails__title">{pageInfo.confirm.title}</div>
          {pageInfo.confirm.content}
        </Modal>
      }

      <If condition={isShowBookingModal}>
        <ReconciliationOpportunityModal
          activeModal
          handleClickClose={handleToggleEditOpportunityModal}
          refreshDetails={() => {
            fetchReconciliationOpportunityDetails();
          }}
          onConfirm={handleClickConfirm}
          editOpportunity={{
            finalMoveInDate: get(opportunityDetail, 'finalMoveInDate') || '',
            finalMoveOutDate: get(opportunityDetail, 'finalMoveOutDate') || '',
            finalPrice: get(opportunityDetail, 'finalPrice') || 0,
            currency: get(opportunityDetail, 'completedXbooking.currency'),
          }}
        />
      </If>

      <If condition={showInvoiceEditor}>
        <InvoiceEditor
          activeModal
          onClose={() => {
            setInvoiceEditor(false);
          }}
          onConfirm={() => {
            fetchReconciliationOpportunityDetails();
          }}
          opportunityDetail={opportunityDetail}
        />
      </If>

      <If condition={showEditNotes}>
        <EditNotes
          activeModal
          onClose={() => {
            setShowEditNotes(false);
          }}
          value={get(opportunityDetail, 'opportunityNote.reconciliationNote') || ''}
          onConfirm={() => {
            fetchReconciliationOpportunityDetails();
          }}
          opportunityDetail={opportunityDetail}
        />
      </If>
    </div>
  ) : null;
};

export default authControl(platformEntity.BOOKINGS_OPPORTUNITIES, entityAction.READ)(Index);
